import { Box } from '@mui/material';
import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

const ResizeHandler = ({
  orientation = 'horizontal',
  onResize,
  initialSize,
  minSize = 50,
  ariaLabel,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState(0);
  const [startSize, setStartSize] = useState(0);

  const handleMouseMove = useCallback(
    (e) => {
      if (isDragging) {
        const currentPos = orientation === 'horizontal' ? e.clientX : e.clientY;
        const diff = currentPos - startPos;
        const newSize = Math.max(minSize, startSize + diff);
        onResize(newSize);
      }
    },
    [isDragging, startPos, startSize, orientation, minSize, onResize]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, [setIsDragging]);

  const handleKeyDown = useCallback(
    (e) => {
      const step = e.shiftKey ? 10 : 1;

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          onResize(Math.max(minSize, startSize + step));
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          onResize(Math.max(minSize, startSize - step));
          break;
        default:
          break;
      }
    },
    [minSize, startSize, onResize]
  );

  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(true);
      setStartPos(orientation === 'horizontal' ? e.clientX : e.clientY);
      setStartSize(initialSize);
    },
    [orientation, initialSize, setIsDragging, setStartPos, setStartSize]
  );

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const isHorizontal = orientation === 'horizontal';

  return (
    <Box
      tabIndex={0}
      aria-label={ariaLabel}
      aria-valuenow={initialSize}
      aria-valuemin={minSize}
      className="resize-handle"
      onKeyDown={handleKeyDown}
      onMouseDown={handleMouseDown}
      style={{
        position: 'absolute',
        ...(isHorizontal
          ? {
              right: -4,
              top: 0,
              height: '65%',
              width: '8px',
              cursor: 'col-resize',
            }
          : {
              bottom: -4,
              left: 19,
              width: '60%',
              height: '8px',
              cursor: 'row-resize',
            }),
        opacity: isDragging ? 1 : 0,
        zIndex: 1000,
        transition: 'opacity 0.2s, background-color 0.2s',
      }}
    />
  );
};

ResizeHandler.propTypes = {
  orientation: PropTypes.oneOf(['horizontal', 'vertical']).isRequired,
  onResize: PropTypes.func.isRequired,
  initialSize: PropTypes.number.isRequired,
  minSize: PropTypes.number.isRequired,
  ariaLabel: PropTypes.string.isRequired,
};

export default ResizeHandler;
